// ─────────────────────────────────────────────────────────────────────────────
// MAP LAYER REGISTRY — single source of truth for the layers panel.
//
// Layers are grouped by TOPIC (not by country and not by data-source kind).
// Each layer declares its `kind` so the panel knows how to toggle it, and an
// optional `countries` / `useCases` filter so the panel can hide layers that
// don't apply in the current context.
//
//   kind: "data"     → backed by parent state in SiteScreener (vector data,
//                      backend-fetched). `dataKey` matches a key in the
//                      `layers` prop passed to LayerControls.
//   kind: "wms"      → upstream WMS GetMap. Has `url` + `wmsLayer`.
//   kind: "tiles"    → backend tile proxy (FI layers go through proxy because
//                      upstream lacks CORS). Has `url`.
//   kind: "geojson"  → backend geojson endpoint. Has `dataUrl`.
//
// Country-aware entries: when a layer has the same conceptual purpose in two
// countries (e.g. parcels, zoning, protected areas) but uses a different data
// source per country, we declare TWO entries with the SAME label, each scoped
// via `countries`. The country filter naturally hides the wrong one so the
// user sees a single toggle that "follows" the active country.
// ─────────────────────────────────────────────────────────────────────────────

export const TOPIC_GROUPS = [
  // ─── Power & Grid ─────────────────────────────────────────────────────────
  {
    id: "power",
    label: "Power & Grid",
    color: "#22c55e",
    layers: [
      { id: "gridConnections", kind: "data", dataKey: "gridConnections",
        label: "Substations",
        description: "NVE substations + Fingrid OSM-derived points" },
      { id: "power_lines", kind: "wms",
        label: "Power Lines",
        countries: ["NO"],
        // NVE's authoritative grid WMS post-migration. Layer order matters:
        // distribution first so the heavier transmission lines paint on top.
        // Distribusjonsnett has MaxScaleDenominator ~9.4k so it only renders
        // at very high zoom; safe to include.
        url: "https://kart.nve.no/enterprise/services/Nettanlegg4/MapServer/WMSServer",
        wmsLayer: "Distribusjonsnett,Sjokabler,Regionalnett_luftledning46171,Transmisjonsnett_luftledning43179",
        description: "Existing transmission, regional, distribution + submarine cables (NVE Nettanlegg4)",
        opacity: 0.85, minzoom: 6 },
      { id: "hydropower_no", kind: "wms",
        label: "Hydropower Plants",
        countries: ["NO"],
        url: "https://kart.nve.no/enterprise/services/Vannkraft1/MapServer/WMSServer",
        wmsLayer: "Vannkraftverk",
        description: "Built hydropower stations · NVE Vannkraft1 (operating + decommissioned)",
        opacity: 0.85, minzoom: 5 },
      { id: "gridConnections_it", kind: "data", dataKey: "gridConnections",
        label: "Substations",
        countries: ["IT"],
        description: "OSM-derived substations (power=substation) — no national Terna feed available yet" },
    ],
  },

  // ─── Natural Hazards ──────────────────────────────────────────────────────
  {
    id: "hazards",
    label: "Natural Hazards",
    color: "#ef4444",
    layers: [
      { id: "flood_zones", kind: "wms",
        label: "Flood Zones (detailed)",
        countries: ["NO"],
        url: "https://kart.nve.no/enterprise/services/Flomsoner2/MapServer/WMSServer",
        wmsLayer: "Flomsone_1000arsflom",
        description: "1000-year flood zones, where mapped (NVE Flomsoner2)",
        opacity: 0.6 },
      { id: "flood_aktsomhet", kind: "wms",
        label: "Flood Caution Areas",
        countries: ["NO"],
        url: "https://kart.nve.no/enterprise/services/Flomaktsomhet/MapServer/WMSServer",
        wmsLayer: "Flom_aktsomhetsomrade",
        description: "Nationwide flood caution areas (NVE aktsomhetskart)",
        opacity: 0.5 },
      { id: "quick_clay", kind: "wms",
        label: "Quick Clay Risk",
        countries: ["NO"],
        url: "https://kart.nve.no/enterprise/services/KvikkleireskredAktsomhet/MapServer/WMSServer",
        wmsLayer: "KvikkleireskredAktsomhet",
        description: "Quick-clay caution areas (NVE KvikkleireskredAktsomhet)",
        opacity: 0.6 },
      { id: "fi_flood", kind: "tiles",
        label: "Flood Risk Zones",
        countries: ["FI"],
        url: "/api/v1/layers/proxy/fi-flood-tiles?bbox={bbox-epsg-3857}",
        description: "Significant flood risk areas (SYKE INSPIRE Luonnonriskialueet)",
        opacity: 0.6 },
      { id: "fi_groundwater", kind: "tiles",
        label: "Groundwater Areas",
        countries: ["FI"],
        url: "/api/v1/layers/proxy/fi-groundwater-tiles?bbox={bbox-epsg-3857}",
        description: "Groundwater areas — discharge constraints for DC cooling (SYKE INSPIRE)",
        opacity: 0.5 },
      { id: "seismic_zones_it", kind: "wms",
        label: "Seismic Zones",
        countries: ["IT"],
        url: "https://sgi2.isprambiente.it/arcgis/services/servizi/sismicita/MapServer/WMSServer",
        wmsLayer: "1",
        description: "Official seismic classification zones 1-4 (INGV/Protezione Civile, OPCM 3274/2003)",
        opacity: 0.55 },
      { id: "landslide_hazard_it", kind: "tiles",
        label: "Landslide Hazard",
        countries: ["IT"],
        // sinacloud.isprambiente.it hosts have no WMS capability enabled —
        // the /export REST operation supports the same bbox-tile templating.
        url: "https://sinacloud.isprambiente.it/arcgisina/rest/services/PAI_Frane/Aree_pericolosita_frana_PAI/MapServer/export?bbox={bbox-epsg-3857}&bboxSR=3857&imageSR=3857&size=256,256&format=png32&transparent=true&f=image&layers=show:0",
        description: "PAI landslide hazard mosaic — P1 (moderate) to P4 (very high) (ISPRA)",
        opacity: 0.6 },
      { id: "flood_hazard_it", kind: "tiles",
        label: "Flood Hazard",
        countries: ["IT"],
        url: "https://sinacloud.isprambiente.it/arcgisina/rest/services/Alluvioni/Aree_pericolosita_idraulica_/MapServer/export?bbox={bbox-epsg-3857}&bboxSR=3857&imageSR=3857&size=256,256&format=png32&transparent=true&f=image&layers=show:0,1,2",
        description: "PAI flood hazard scenarios P1/P2/P3 (ISPRA)",
        opacity: 0.55 },
    ],
  },

  // ─── Property & Zoning ────────────────────────────────────────────────────
  {
    id: "property",
    label: "Property & Zoning",
    color: "#f97316",
    layers: [
      { id: "parcels", kind: "data", dataKey: "parcels",
        label: "Parcel Boundaries",
        countries: ["NO"],
        description: "Matrikkelen cadastral parcels (zoom 13+)" },
      { id: "fi_parcels", kind: "tiles",
        label: "Parcel Boundaries",
        countries: ["FI"],
        url: "/api/v1/layers/proxy/fi-parcels-tiles?bbox={bbox-epsg-3857}",
        description: "Cadastral parcel boundaries from MML kiinteistö (zoom 13+)",
        opacity: 0.8, minzoom: 13 },
      { id: "zoning", kind: "data", dataKey: "zoning",
        label: "Zoning Plans",
        countries: ["NO"],
        description: "DIBK NAP — kommuneplan + reguleringsplan (click for details)" },
      { id: "fi_zoning", kind: "tiles",
        label: "Zoning Plans",
        countries: ["FI"],
        url: "/api/v1/layers/proxy/fi-zoning-tiles?bbox={bbox-epsg-3857}",
        description: "Asemakaava + yleiskaava index — coverage expanding (SYKE Ryhti)",
        opacity: 0.55 },
      { id: "land_cover_it", kind: "tiles",
        label: "Land Cover (zoning proxy)",
        countries: ["IT"],
        // Italy has no national open zoning (PRG/PGT) dataset — each comune
        // publishes independently, no standard format. CORINE Land Cover is
        // an interim national-coverage proxy only, not real zoning permission.
        url: "https://sinacloud.isprambiente.it/arcgisina/rest/services/corine_land_cover/CorineLandCover/MapServer/export?bbox={bbox-epsg-3857}&bboxSR=3857&imageSR=3857&size=256,256&format=png32&transparent=true&f=image&layers=show:5",
        description: "CORINE Land Cover 2018 — interim buildability proxy, not real municipal zoning (ISPRA)",
        opacity: 0.5 },
      { id: "built_up_areas", kind: "wms",
        label: "Settlement Areas",
        countries: ["NO"],
        url: "https://kart.ssb.no/api/mapserver/v1/wms/tettsteder",
        wmsLayer: "tettsted_2025",
        description: "SSB urban agglomeration boundaries — affects social licence and NH3 safety zone scoring",
        opacity: 0.45, minzoom: 8 },
    ],
  },

  // ─── Environment ──────────────────────────────────────────────────────────
  {
    id: "environment",
    label: "Environment",
    color: "#dc2626",
    layers: [
      { id: "environmentalConstraints", kind: "data", dataKey: "environmentalConstraints",
        label: "Protected Areas",
        countries: ["NO"],
        description: "National parks, nature reserves, Natura 2000 (Miljødirektoratet)" },
      { id: "fi_protected_areas", kind: "tiles",
        label: "Protected Areas",
        countries: ["FI"],
        url: "/api/v1/layers/proxy/fi-protected-areas-tiles?bbox={bbox-epsg-3857}",
        description: "National parks, nature reserves, Natura 2000 SAC/SPA, wilderness (SYKE INSPIRE)",
        opacity: 0.65 },
      { id: "nature_types_nin", kind: "wms",
        label: "Nature Types (NiN)",
        countries: ["NO"],
        url: "https://wms.miljodirektoratet.no/arcgis/services/naturtyper_nin/MapServer/WmsServer",
        wmsLayer: "naturtype_nin_alle",
        description: "Nature type A and B mapping (Miljødirektoratet)",
        opacity: 0.65 },
      { id: "salmon_rivers", kind: "wms",
        label: "Salmon Rivers",
        countries: ["NO"],
        url: "https://kart.miljodirektoratet.no/arcgis/services/anadrome_laksefisk/MapServer/WMSServer",
        wmsLayer: "4,9",
        description: "Anadromous salmon/sea trout rivers (Miljødirektoratet)",
        opacity: 0.75 },
      // 2026-10-04: Riksantikvaren's Kulturminner WMS (kart.ra.no/arcgis/
      // services/Distribusjon/Kulturminner20180301/MapServer/WmsServer) is
      // returning HTTP 404 on every GetMap tile request. The console-noise
      // from thousands of failed tile fetches degrades every search session,
      // and there's no fallback endpoint published by Riksantikvaren. Entry
      // removed until a working endpoint is announced — restore here when it
      // is. (The FI counterpart below is served by our Museovirasto proxy
      // and is unaffected.)
      { id: "fi_cultural_heritage", kind: "tiles",
        label: "Cultural Heritage",
        countries: ["FI"],
        url: "/api/v1/layers/proxy/fi-heritage-tiles?bbox={bbox-epsg-3857}",
        description: "Ancient monuments + nationally significant built heritage (Museovirasto)",
        opacity: 0.7, minzoom: 8 },
    ],
  },

  // ─── Subsurface & Geology ─────────────────────────────────────────────────
  {
    id: "geology",
    label: "Subsurface & Geology",
    color: "#a855f7",
    layers: [
      { id: "ar5", kind: "wms",
        label: "AR5 Land Cover",
        countries: ["NO"],
        url: "https://wms.nibio.no/cgi-bin/ar5",
        wmsLayer: "Arealtype",
        description: "Cultivated land, forest, bog, built-up etc. (NIBIO) — zoom 13+",
        opacity: 0.55, minzoom: 13 },
      { id: "losmasser", kind: "wms",
        label: "Surface Deposits",
        countries: ["NO"],
        url: "https://geo.ngu.no/mapserver/LosmasserWMS3",
        wmsLayer: "Losmasser_temakart_sammenstilt",
        description: "Superficial deposits — affects foundation cost (NGU)",
        opacity: 0.55 },
      { id: "berggrunn", kind: "wms",
        label: "Bedrock Type",
        countries: ["NO"],
        url: "https://geo.ngu.no/mapserver/BerggrunnWMS3",
        wmsLayer: "Berggrunn_nasjonal_flater",
        description: "Bedrock geology — affects blasting/excavation cost (NGU)",
        opacity: 0.50 },
      { id: "mulig_marin_leire", kind: "wms",
        label: "Possible Marine Clay",
        countries: ["NO"],
        url: "https://geo.ngu.no/mapserver/MarinGrenseWMS4",
        wmsLayer: "Mulig_marin_leire_losmasseflater_klassifisert",
        description: "Classified probability of marine clay in surface deposits (NGU) — most actionable",
        opacity: 0.55 },
      { id: "marin_grense_under", kind: "wms",
        label: "Marine Limit Extent",
        countries: ["NO"],
        url: "https://geo.ngu.no/mapserver/MarinGrenseWMS4",
        wmsLayer: "Marin_grense_flater_areal_under",
        description: "Geographic context: areas below the Ice-Age marine limit (NGU)",
        opacity: 0.45 },
    ],
  },

  // ─── Logistics & Industry ─────────────────────────────────────────────────
  {
    id: "logistics",
    label: "Logistics & Industry",
    color: "#06b6d4",
    layers: [
      { id: "ports", kind: "data", dataKey: "ports",
        label: "Ports & Quays",
        countries: ["NO"],
        // Hydrogen-only — port proximity matters for NH₃/LH₂/MeOH
        // export shipping. Data centres don't move physical product;
        // the few cases where DC siting touches ports (seawater intake
        // for liquid cooling, hyperscale equipment delivery) are
        // niche enough that the marker clutter outweighs the value.
        // Same pattern as the CO₂ Sources scope (Mar-26 commit e143656).
        useCases: ["hydrogen"],
        description: "Kystdatahuset commercial port and quay points (Norway only)" },
      { id: "co2Sources", kind: "data", dataKey: "co2Sources",
        label: "CO₂ Sources",
        countries: ["NO"],
        // Hydrogen-only — CO₂ off-take only matters for methanol
        // synthesis (and other CCU pathways). Data centres don't
        // consume CO₂, so this layer just clutters the DC map.
        useCases: ["hydrogen"],
        description: "Major Norwegian industrial CO₂ emitters (ETS)" },
      { id: "existing_datacenters", kind: "geojson",
        label: "Existing Data Centers",
        useCases: ["data_center"],
        dataUrl: "/api/v1/layers/existing-datacenters",
        description: "Operating Nordic data centres — hyperscale and colocation" },
      { id: "ixp_locations", kind: "geojson",
        label: "Fiber IXPs",
        useCases: ["data_center"],
        dataUrl: "/api/v1/layers/ixp-locations",
        description: "Internet Exchange Points — latency reference for DC use-case" },
      { id: "peering_facilities", kind: "geojson",
        label: "Peering Facilities (PeeringDB)",
        useCases: ["data_center"],
        dataUrl: "/api/v1/layers/peering-facilities",
        description: "Live PeeringDB IXPs + colocation facilities for NO + FI (cached 24h)" },
      { id: "dh_plants_fi", kind: "geojson",
        label: "DH Plants",
        countries: ["FI"], useCases: ["data_center"],
        dataUrl: "/api/v1/layers/dh-plants-fi",
        description: "District heating plants — waste heat offtake potential (Finnish Energy 2024)" },
    ],
  },

  // ─── Partner Networks ─────────────────────────────────────────────────────
  {
    id: "partners",
    label: "Partner Networks",
    color: "#0ea5e9",
    layers: [
      { id: "ultranet_carrier_sites", kind: "geojson",
        label: "Ultranet Carrier Sites",
        countries: ["IT"],
        dataUrl: "/api/v1/layers/ultranet-carrier-sites",
        description: "Ultranet Italia partner network — Centraline (cabinets) + Infrastrutture (PoP sites)" },
      { id: "ultranet_backbone", kind: "geojson",
        label: "Ultranet Backbone Routes",
        countries: ["IT"],
        dataUrl: "/api/v1/layers/ultranet-backbone",
        description: "Ultranet Italia partner fiber backbone (Dorsale) routes" },
    ],
  },
];

// Layers ON by default. (Vector data layers are managed by parent state and
// default to visible there; this list governs WMS/tiles/geojson defaults.)
export const DEFAULT_ON_LAYERS = [
  "power_lines",
  "flood_zones",
  "ultranet_carrier_sites",
  "ultranet_backbone",
];

// ─────────────────────────────────────────────────────────────────────────────
// LEGACY EXPORTS — derived from TOPIC_GROUPS for the WMS rendering loop in
// SiteScreener.jsx (which iterates LAYER_GROUPS / FINLAND_LAYER_GROUPS /
// DATACENTER_LAYER_GROUPS to look up tile URLs by id). The rendering loop
// only needs entries with a `url` (wms/tiles), so we map our unified entries
// back to the older shape it expects:
//   { id, label, type: "wms" | "tiles", url, wmsLayer, ... }
// Data-kind entries are excluded — they're rendered through parent state.
// ─────────────────────────────────────────────────────────────────────────────

function _toLegacyLayer(l) {
  if (l.kind === "wms") {
    return { id: l.id, label: l.label, type: "wms",
             url: l.url, wmsLayer: l.wmsLayer, wmsVersion: l.wmsVersion,
             description: l.description, opacity: l.opacity, minzoom: l.minzoom,
             country: (l.countries && l.countries.length === 1) ? l.countries[0] : undefined };
  }
  if (l.kind === "tiles") {
    return { id: l.id, label: l.label, type: "tiles",
             url: l.url, description: l.description,
             opacity: l.opacity, minzoom: l.minzoom,
             country: (l.countries && l.countries.length === 1) ? l.countries[0] : undefined };
  }
  if (l.kind === "geojson") {
    return { id: l.id, label: l.label, type: "geojson",
             dataUrl: l.dataUrl, description: l.description,
             country: (l.countries && l.countries.length === 1) ? l.countries[0] : "ALL" };
  }
  return null;
}

function _legacyGroupsFor(predicate) {
  return TOPIC_GROUPS.map(g => ({
    id: g.id, label: g.label,
    layers: g.layers.filter(predicate).map(_toLegacyLayer).filter(Boolean),
  })).filter(g => g.layers.length > 0);
}

const _isNoTileLayer = l =>
  (l.kind === "wms" || l.kind === "tiles") &&
  (!l.countries || l.countries.includes("NO"));

const _isFiTileLayer = l =>
  (l.kind === "wms" || l.kind === "tiles") &&
  l.countries && l.countries.includes("FI") && !l.countries.includes("NO");

const _isItTileLayer = l =>
  (l.kind === "wms" || l.kind === "tiles") &&
  l.countries && l.countries.includes("IT") && !l.countries.includes("NO");

const _isDcLayer = l =>
  l.useCases && l.useCases.includes("data_center");

export const LAYER_GROUPS = _legacyGroupsFor(_isNoTileLayer);
export const FINLAND_LAYER_GROUPS = _legacyGroupsFor(_isFiTileLayer);
export const ITALY_LAYER_GROUPS = _legacyGroupsFor(_isItTileLayer);
export const DATACENTER_LAYER_GROUPS = _legacyGroupsFor(_isDcLayer);
